import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Modal, ModalBody, ModalFooter } from "./Modal";
import { Button } from "./Button";

const ConfirmContext = createContext(null);

/**
 * ConfirmDialogProvider expone `useConfirm()` que devuelve una función
 * imperativa `confirm({ title, description, confirmLabel, cancelLabel, tone })`
 * y resuelve a `true` si el usuario confirma, `false` si cancela.
 *
 * Sustituye al `window.confirm` nativo manteniendo la estética del DS.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "¿Eliminar?", description: "...", tone: "danger" })) { ... }
 */
export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: options.title || "¿Confirmar?",
        description: options.description || null,
        confirmLabel: options.confirmLabel || "Confirmar",
        cancelLabel: options.cancelLabel || "Cancelar",
        tone: options.tone === "danger" ? "danger" : "primary",
      });
    });
  }, []);

  const close = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state.open}
        onClose={() => close(false)}
        title={state.title}
        size="sm"
      >
        <ModalBody>
          {state.description && (
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                color: "var(--kr-fg-primary)",
              }}
            >
              {state.description}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={() => close(false)} autoFocus>
            {state.cancelLabel}
          </Button>
          <Button
            variant={state.tone === "danger" ? "danger" : "primary"}
            type="button"
            onClick={() => close(true)}
          >
            {state.confirmLabel}
          </Button>
        </ModalFooter>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return ctx;
}

export default ConfirmDialogProvider;
